<template>
    <div class="foxy-card h-full cursor-pointer !rounded-2xl p-4 shadow-md backdrop-blur-xl !duration-300 hover:!shadow-lg"
         @click="openLibrary">
        <!-- Header with author info -->
        <div class="mb-3 flex min-w-0 items-center justify-between gap-2">
            <div class="flex min-w-0 items-center space-x-2.5">
                <!-- Avatar -->
                <user-avatar
                    :src="library.user.avatar || ''"
                    :name="library.user.name"
                    size="sm"
                    class="bg-gradient-to-br from-indigo-500 to-purple-600 text-xs font-bold text-white"
                />
                <div class="min-w-0">
                    <p class="truncate text-sm font-medium text-foxy-text">{{ library.user.name }}</p>
                    <p class="foxy-muted text-xs">{{ formatDate(library.created_at) }}</p>
                </div>
            </div>
            <span v-if="library.is_private" class="shrink-0 rounded-full bg-foxy-elevated px-2 py-1 text-xs font-semibold text-foxy-text-secondary">Приватна</span>
        </div>

        <!-- Library title -->
        <h3 class="mb-3 line-clamp-2 text-lg font-bold leading-snug text-foxy-text">{{ library.name }}</h3>

        <!-- Library description -->
        <!-- <p v-if="library.description" class="foxy-muted text-sm mb-4 line-clamp-2">{{ library.description }}</p> -->

        <!-- Books preview -->
        <div class="mb-3">
            <div class="grid grid-cols-3 gap-2">
                <!-- First book cover -->
                <div v-if="library.books && library.books.length > 0" class="aspect-[2/3] overflow-hidden rounded-lg bg-foxy-elevated shadow-sm">
                    <img :src="library.books[0].cover_image || bookCoverPlaceholder"
                         :alt="library.books[0].title"
                         loading="lazy"
                         decoding="async"
                         width="120"
                         height="180"
                         class="h-full w-full object-cover">
                </div>
                <div v-else class="flex aspect-[2/3] items-center justify-center rounded-lg bg-foxy-elevated">
                    <svg class="h-7 w-7 text-foxy-text-secondary" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"/>
                    </svg>
                </div>

                <!-- Second book cover -->
                <div v-if="library.books && library.books.length > 1" class="aspect-[2/3] overflow-hidden rounded-lg bg-foxy-elevated shadow-sm">
                    <img :src="library.books[1].cover_image || bookCoverPlaceholder"
                         :alt="library.books[1].title"
                         loading="lazy"
                         decoding="async"
                         width="120"
                         height="180"
                         class="h-full w-full object-cover">
                </div>
                <div v-else class="flex aspect-[2/3] items-center justify-center rounded-lg bg-foxy-elevated">
                    <svg class="h-7 w-7 text-foxy-text-secondary" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"/>
                    </svg>
                </div>

                <!-- Additional books counter -->
                <div v-if="library.books_count > 2"
                     class="flex aspect-[2/3] items-center justify-center rounded-lg bg-foxy-elevated">
                    <span class="text-sm font-bold text-foxy-action">+{{ library.books_count - 2 }}</span>
                </div>
                <div v-else class="flex aspect-[2/3] items-center justify-center rounded-lg bg-foxy-elevated">
                    <svg class="h-7 w-7 text-foxy-text-secondary" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"/>
                    </svg>
                </div>
            </div>
        </div>

        <!-- Actions -->
        <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="flex min-w-0 items-center gap-3">
                <!-- Like button -->
                <button v-if="!isOwner" @click.stop="toggleLike"
                        class="flex items-center space-x-1 text-foxy-text-secondary hover:text-red-500 transition-colors">
                    <svg class="w-4 h-4" :class="{ 'text-red-500': isLiked }" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clip-rule="evenodd"/>
                    </svg>
                    <span class="text-sm">{{ likesCount }}</span>
                </button>
                <span v-else class="flex items-center space-x-1 text-foxy-text-secondary" title="Кількість вподобань">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clip-rule="evenodd"/>
                    </svg>
                    <span class="text-sm">{{ likesCount }}</span>
                </span>

                <!-- Save button -->
                <button v-if="canSave" @click.stop="toggleSave"
                        class="foxy-btn-secondary rounded-lg px-2.5 py-1.5 text-sm"
                        :aria-pressed="isSaved ? 'true' : 'false'"
                        :aria-busy="savePending ? 'true' : 'false'"
                        :aria-label="isSaved ? 'Прибрати зі збереженого' : 'Зберегти'"
                        :title="isSaved ? 'Прибрати зі збереженого' : 'Зберегти'"
                        :disabled="savePending">
                    <svg class="h-4 w-4" :fill="isSaved ? 'currentColor' : 'none'" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                    </svg>
                    <span>{{ isSaved ? 'Збережено' : 'Зберегти' }}</span>
                </button>
            </div>

            <!-- Share button -->
            <button @click.stop="shareLibrary" 
                    class="flex shrink-0 items-center space-x-1 text-foxy-text-secondary transition-colors hover:text-green-500">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                </svg>
                <span class="text-sm">Поділитись</span>
            </button>
        </div>

        <div v-if="isOwner" class="foxy-divider mt-3 flex flex-wrap items-center gap-1.5 border-t pt-3">
            <button type="button" class="rounded-lg bg-foxy-action px-2.5 py-1.5 text-sm font-semibold text-white hover:bg-foxy-action/90" @click.stop="$emit('manage', library)">Керувати книгами</button>
            <a :href="library.edit_url" class="rounded-lg bg-foxy-elevated px-2.5 py-1.5 text-sm font-semibold text-foxy-text-secondary hover:bg-foxy-surface hover:text-foxy-text" @click.stop>Редагувати</a>
            <button type="button" class="rounded-lg px-2.5 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30" @click.stop="$emit('delete', library)">Видалити</button>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'LibraryCollection',
    props: {
        library: {
            type: Object,
            required: true
        },
        isAuthenticated: {
            type: Boolean,
            default: false
        },
        isOwner: {
            type: Boolean,
            default: false
        },
        isLiked: {
            type: Boolean,
            default: false
        },
        isSaved: {
            type: Boolean,
            default: false
        },
        canSave: {
            type: Boolean,
            default: false
        },
        likesCount: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            savePending: false,
            bookCoverPlaceholder: (window.imagePlaceholders && window.imagePlaceholders.bookCover)
                ? window.imagePlaceholders.bookCover
                : '/images/placeholders/book-cover.svg'
        }
    },
    methods: {
        formatDate(date) {
            const d = new Date(date);
            return d.toLocaleDateString('uk-UA');
        },
        openLibrary() {
            if (this.library.slug && this.library.user && this.library.user.username) {
                window.location.href = `/users/${this.library.user.username}/libraries/${this.library.slug}`;
            } else {
                // Fallback для старых данных
                window.location.href = `/libraries/${this.library.id}`;
            }
        },
        toggleMenu() {
            // TODO: Implement menu functionality
            console.log('Toggle menu for library:', this.library.id);
        },
        async toggleLike() {
            if (!this.isAuthenticated || !this.library.slug) {
                return;
            }

            try {
                const response = await axios.post(`/libraries/${this.library.slug}/like`);
                if (response.data.success) {
                    this.$emit('liked', {
                        libraryId: this.library.id,
                        isLiked: response.data.is_liked,
                        likesCount: response.data.likes_count
                    });
                }
            } catch (error) {
                console.error('Error toggling like:', error);
            }
        },
        async toggleSave() {
            if (!this.canSave || !this.library.slug || this.savePending) {
                return;
            }

            this.savePending = true;
            try {
                const response = await axios.post(`/libraries/${encodeURIComponent(this.library.slug)}/save`, {}, {
                    headers: {
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                });
                
                if (response.data.success) {
                    this.$emit('saved', {
                        libraryId: this.library.id,
                        isSaved: response.data.is_saved
                    });
                }
            } catch (error) {
                console.error('Error toggling save:', error);
            } finally {
                this.savePending = false;
            }
        },
        async shareLibrary() {
            const { shareContent } = await import('../utils/shareHelper');
            let url;
            if (this.library.slug && this.library.user && this.library.user.username) {
                url = `${window.location.origin}/users/${this.library.user.username}/libraries/${this.library.slug}`;
            } else {
                // Fallback для старых данных
                url = `${window.location.origin}/libraries/${this.library.id}`;
            }
            await shareContent({
                title: this.library.name,
                text: this.library.description || `Добірка "${this.library.name}" від ${this.library.user.name}`,
                url: url
            });
        }
    }
};
</script>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
